import { isAxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import {
  REQUEST_ID_HEADER,
  TRACEPARENT_HEADER,
  parseTraceparent,
} from '@/lib/telemetry/trace-context';

// ─── Error codes ──────────────────────────────────────────────────────────────

export const SESSION_EXPIRED_MESSAGE = 'Session expired. Please log in again.';

export type ApiErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'CANCELED'
  | 'UNKNOWN';

export interface FieldValidationError {
  field: string;
  message: string;
  severity?: 'error' | 'warning';
}

// ─── ApiError ─────────────────────────────────────────────────────────────────

/**
 * Normalized error shape returned by all API clients.
 * React Query error handlers receive this typed object.
 */
export interface ApiErrorTelemetry {
  /**
   * The `x-request-id` the SPA sent with the failed request. The backend logs
   * and echoes it, so this is the token to hand to support / paste into Logs
   * Insights. Present for every failure that got as far as building a request.
   */
  requestId?: string;
  /**
   * The W3C trace id the SPA put on the request — known only when the SPA
   * minted the `traceparent` itself (CloudWatch RUM inactive). When RUM owns
   * the header the id lives in RUM's own X-Ray segment instead.
   */
  traceId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  override readonly message: string;
  /** Raw response body, useful for validation error details */
  readonly data?: unknown;
  readonly requestId?: string;
  readonly traceId?: string;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    data?: unknown,
    telemetry?: ApiErrorTelemetry,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.message = message;
    this.data = data;
    this.requestId = telemetry?.requestId;
    this.traceId = telemetry?.traceId;
  }

  /** Per-field validation errors returned by API validation failures. */
  get fieldErrors(): FieldValidationError[] | undefined {
    const raw = this.data as { errors?: unknown } | undefined;
    if (!raw || !Array.isArray(raw.errors)) return undefined;

    const deduped = new Map<string, FieldValidationError>();
    for (const entry of raw.errors) {
      if (typeof entry !== 'object' || entry === null) continue;
      const candidate = entry as {
        field?: unknown;
        message?: unknown;
        severity?: unknown;
      };
      if (typeof candidate.field !== 'string' || typeof candidate.message !== 'string') {
        continue;
      }
      const next = {
        field: candidate.field,
        message: candidate.message,
        severity:
          candidate.severity === 'warning' || candidate.severity === 'error'
            ? candidate.severity
            : 'error',
      } satisfies FieldValidationError;
      const current = deduped.get(candidate.field);
      if (
        !current ||
        validationMessagePriority(next.message) < validationMessagePriority(current.message)
      ) {
        deduped.set(candidate.field, next);
      }
    }

    return deduped.size > 0 ? Array.from(deduped.values()) : undefined;
  }
}

function validationMessagePriority(message: string): number {
  if (message.endsWith('is required.')) return 0;
  if (message.endsWith('cannot be empty.')) return 1;
  if (message.includes('must be text') || message.includes('must be a number')) return 4;
  return 2;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type HeaderBag = AxiosRequestConfig['headers'] | AxiosResponse['headers'] | undefined;

/** Case-insensitive header read that works for AxiosHeaders and plain objects. */
function readHeader(headers: HeaderBag, name: string): string | undefined {
  if (!headers) return undefined;
  const bag = headers as { get?: (key: string) => unknown } & Record<string, unknown>;
  const value =
    typeof bag.get === 'function'
      ? bag.get(name)
      : (bag[name] ?? bag[Object.keys(bag).find((k) => k.toLowerCase() === name) ?? '']);
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The correlation ids for a request: the SPA's `x-request-id` (or the
 * server's echo of it) and, when the SPA set the `traceparent`, its trace id.
 */
export function telemetryFromRequest(
  config: AxiosRequestConfig | undefined,
  response?: AxiosResponse | undefined,
): ApiErrorTelemetry {
  const requestId =
    readHeader(response?.headers, REQUEST_ID_HEADER) ??
    readHeader(config?.headers, REQUEST_ID_HEADER);
  const traceId = parseTraceparent(readHeader(config?.headers, TRACEPARENT_HEADER))?.traceId;
  return { requestId, traceId };
}

/**
 * The line a toast / log appends so the operator can quote the failing
 * request: `Request ID <uuid>`. Undefined when no request was ever built.
 */
export function requestReference(error: ApiError): string | undefined {
  return error.requestId ? `Request ID ${error.requestId}` : undefined;
}

/** The `ApiErrorCode` an HTTP status maps to; shared by the axios client and raw `fetch` callers. */
export function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 400 || status === 422) return 'VALIDATION_ERROR';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN';
}

/**
 * Normalizes any thrown value (Axios error, plain Error, unknown) into ApiError.
 * Called by response interceptors so all consumers deal with a single error type.
 */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (isAxiosError(error)) {
    const telemetry = telemetryFromRequest(error.config, error.response);

    // React Query cancellation (component unmount / navigation) — not a timeout
    if (error.code === 'ERR_CANCELED') {
      return new ApiError(0, 'CANCELED', 'Request was cancelled', undefined, telemetry);
    }

    // Axios timeout (ECONNABORTED)
    if (error.code === 'ECONNABORTED') {
      return new ApiError(0, 'TIMEOUT', 'Request timed out after 30 seconds', undefined, telemetry);
    }

    // No response — network failure
    if (!error.response) {
      return new ApiError(
        0,
        'NETWORK_ERROR',
        error.message || 'Network error — check your connection',
        undefined,
        telemetry,
      );
    }

    const { status, data } = error.response;
    const raw = data as Record<string, unknown> | null;
    // NOT-76: Nest ValidationPipe failures surface `message` as `string[]`
    // (the array of per-field class-validator strings) rather than a
    // single string. Join those into a human-readable line so the toast
    // layer shows the actual reason ("Phone number must be in E.164
    // format.") instead of the generic axios "Request failed with status
    // code 400" fallback.
    const rawMessage = raw?.message;
    const messageFromRaw =
      typeof rawMessage === 'string'
        ? rawMessage
        : Array.isArray(rawMessage)
          ? (rawMessage as unknown[])
              .filter((entry): entry is string => typeof entry === 'string')
              .join('\n') || undefined
          : undefined;
    const message =
      messageFromRaw ??
      (typeof raw?.error === 'string' ? raw.error : undefined) ??
      error.message ??
      'An unexpected error occurred';

    return new ApiError(status, statusToCode(status), message, data, telemetry);
  }

  if (error instanceof Error) {
    return new ApiError(0, 'UNKNOWN', error.message);
  }

  return new ApiError(0, 'UNKNOWN', 'An unexpected error occurred');
}

/** Type guard for use in React Query error handlers */
/**
 * A 400 is a VERDICT, not a hiccup.
 *
 * The same request produces the same 400 forever: the server has read it and
 * refused it. So a caller must neither retry it nor poll it — doing both is
 * how a rejected filter turned into three requests every thirty seconds,
 * indefinitely, behind an error card the operator could not clear.
 *
 * Lives here rather than beside one hook because it is a property of the
 * status code, not of any endpoint. `useReviewStories` proved the rule on the
 * stories cursor; `useUnifiedReviews` needed exactly the same thing.
 */
export function isBadRequest(error: unknown): boolean {
  return isApiError(error) && error.status === 400;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
